import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Download, Paperclip, XCircle } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import { toast } from "sonner";
import { eur } from "@/components/accounting/docUtils";
import { fmtD } from "@/components/graphisme/gUtils";

export function ElementUploader({ element, onDone, compact = false }) {
  const [file, setFile] = useState(null);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const submit = async () => {
    if (!file && !comment.trim()) { toast.error("Joignez un fichier ou un commentaire"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      if (file) fd.append("file", file);
      if (comment.trim()) fd.append("comment", comment.trim());
      await api.post(`/graphisme/espace/elements/${element.id}`, fd);
      toast.success(`« ${element.label} » transmis à votre graphiste`);
      setFile(null); setComment("");
      onDone?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur lors de l'envoi"); }
    finally { setBusy(false); }
  };

  return (
    <div className={`space-y-2 ${compact ? "" : "mt-2 pt-2 border-t border-orange-100"}`} data-testid={`element-uploader-${element.id}`}>
      <div className="flex gap-2 flex-wrap items-center">
        <input ref={fileRef} type="file" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)}
          data-testid={`element-file-${element.id}`} />
        <Button type="button" variant="outline" size="sm" className="h-8 text-xs gap-1.5"
          onClick={() => fileRef.current?.click()} data-testid={`element-file-btn-${element.id}`}>
          <Paperclip className="w-3.5 h-3.5" /> {file ? file.name : "Joindre un fichier"}
        </Button>
        <Input className="flex-1 min-w-[160px] !h-8 text-xs" placeholder="Commentaire (optionnel)"
          value={comment} onChange={(e) => setComment(e.target.value)} data-testid={`element-comment-${element.id}`} />
        <Button size="sm" className="h-8 text-xs bg-neutral-900 hover:bg-neutral-800 text-white"
          disabled={busy || (!file && !comment.trim())} onClick={submit} data-testid={`element-send-${element.id}`}>
          {busy ? "Envoi…" : "Valider l'envoi"}
        </Button>
      </div>
    </div>
  );
}

export default function GraphismeEspaceDevis() {
  const { qid } = useParams();
  const navigate = useNavigate();
  const [q, setQ] = useState(null);
  const [mode, setMode] = useState(null); // null | "refuse"
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [elementsPopup, setElementsPopup] = useState(null);

  const load = () => api.get(`/graphisme/espace/devis/${qid}`).then((r) => setQ(r.data))
    .catch(() => navigate("/graphisme/espace"));
  useEffect(() => { load(); }, [qid]);

  const answer = async (action) => {
    if (action !== "valide" && !comment.trim()) {
      toast.error("Merci d'expliquer votre demande dans le commentaire"); return;
    }
    setBusy(true);
    try {
      const r = await api.post(`/graphisme/espace/devis/${qid}/answer`,
        { action, message: comment.trim() });
      if (action === "valide") {
        toast.success("Devis validé — merci ! Votre projet démarre.");
        if (r.data.elements_pending?.length) { setElementsPopup(r.data.elements_pending); await load(); return; }
        navigate("/graphisme/espace");
      } else {
        toast.success(action === "refuse" ? "Votre réponse a été transmise." : "Votre demande de modification a été transmise.");
        navigate("/graphisme/espace");
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  if (!q) return (
    <div className="min-h-screen bg-neutral-100 flex items-center justify-center">
      <div className="w-6 h-6 border-2 border-neutral-300 border-t-neutral-900 rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="min-h-screen bg-neutral-100" data-testid="gclient-devis-page">
      <header className="bg-white border-b border-neutral-200">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center gap-3">
          <Button variant="outline" size="sm" className="gap-1" data-testid="gclient-devis-back"
            onClick={() => navigate("/graphisme/espace")}>
            <ArrowLeft className="w-3.5 h-3.5" /> Retour
          </Button>
          <img src="/logo-dga.png" alt="DGA" className="w-8 h-8 rounded-lg object-cover" />
          <p className="font-heading font-bold text-sm">Devis {q.number}</p>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6 space-y-5">
        <div className="bg-white border border-neutral-200 rounded-2xl p-6 space-y-4" data-testid="gclient-devis-card">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h1 className="font-heading font-bold text-xl">Devis {q.number}</h1>
              <p className="text-xs text-neutral-400">Émis le {fmtD(q.created_at)}{q.validity_days ? ` · valable ${q.validity_days} jours` : ""}</p>
            </div>
            {q.validated && (
              <span className="text-xs bg-green-50 text-green-700 border border-green-200 rounded-full px-3 py-1" data-testid="gclient-devis-validated">✓ Validé</span>
            )}
          </div>

          <div className="divide-y divide-neutral-100" data-testid="gclient-devis-items">
            {(q.items || []).map((it, i) => (
              <div key={i} className="py-2.5 flex justify-between gap-3 text-sm">
                <div className="min-w-0">
                  <p className="font-medium">{it.prestation}</p>
                  {it.description && <p className="text-xs text-neutral-400 whitespace-pre-line">{it.description}</p>}
                </div>
                <p className="text-neutral-600 whitespace-nowrap">{it.qty} × {eur(it.price_ht)}</p>
              </div>
            ))}
          </div>

          <div className="text-sm space-y-1 pt-2 border-t border-neutral-100">
            <p className="flex justify-between text-neutral-500"><span>Total HT</span><span>{eur(q.total_ht)}</span></p>
            {(q.total_tva || 0) > 0 && <p className="flex justify-between text-neutral-500"><span>TVA</span><span>{eur(q.total_tva)}</span></p>}
            <p className="flex justify-between font-heading font-bold text-lg"><span>Total</span><span data-testid="gclient-devis-total">{eur(q.total_ttc)}</span></p>
            {q.acompte_type && q.acompte_type !== "aucun" && (
              <p className="flex justify-between text-amber-700 text-xs pt-1">
                <span>Acompte demandé ({q.acompte_type === "pourcent" ? `${q.acompte_value} %` : "montant fixe"})</span>
                <span>{q.acompte_type === "pourcent" ? eur((q.total_ttc || 0) * q.acompte_value / 100) : eur(q.acompte_value)}</span>
              </p>
            )}
          </div>

          {q.comment && <p className="text-xs text-neutral-500 bg-neutral-50 border border-neutral-100 rounded-lg p-3 whitespace-pre-line">{q.comment}</p>}

          <a href={`/api/graphisme/espace/devis/${q.id}/pdf`} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-2 text-sm border border-neutral-200 rounded-lg px-4 py-2.5 hover:border-neutral-400 transition-colors w-full justify-center"
            data-testid="gclient-devis-pdf">
            <Download className="w-4 h-4" /> Télécharger le devis (PDF)
          </a>
        </div>

        {!q.answered && (
          <div className="bg-white border border-neutral-200 rounded-2xl p-6 space-y-3" data-testid="gclient-devis-actions">
            {mode === "refuse" ? (
              <>
                <p className="text-sm font-medium">Expliquez les modifications souhaitées ou la raison du refus :</p>
                <Textarea rows={4} value={comment} onChange={(e) => setComment(e.target.value)}
                  placeholder="Ex : pouvez-vous ajuster…" data-testid="gclient-devis-comment" autoFocus />
                <div className="flex gap-2">
                  <Button variant="outline" className="flex-1" onClick={() => { setMode(null); setComment(""); }} data-testid="gclient-devis-cancel-refuse">Annuler</Button>
                  <Button className="flex-1 bg-red-600 hover:bg-red-700 text-white" disabled={busy || !comment.trim()}
                    onClick={() => answer("modification")} data-testid="gclient-devis-send-modif">
                    {busy ? "Envoi…" : "Envoyer ma demande"}
                  </Button>
                </div>
                <button className="w-full text-xs text-red-500 hover:underline" disabled={busy || !comment.trim()}
                  onClick={() => answer("refuse")} data-testid="gclient-devis-send-refuse">
                  Refuser définitivement ce devis
                </button>
              </>
            ) : (
              <div className="flex gap-2 flex-col sm:flex-row">
                <Button className="flex-1 bg-green-700 hover:bg-green-800 text-white h-11 gap-2" disabled={busy}
                  onClick={() => answer("valide")} data-testid="gclient-devis-valider">
                  <CheckCircle2 className="w-4 h-4" /> {busy ? "Validation…" : "Valider le devis"}
                </Button>
                <Button variant="outline" className="flex-1 h-11 gap-2" disabled={busy}
                  onClick={() => setMode("refuse")} data-testid="gclient-devis-refuser">
                  <XCircle className="w-4 h-4" /> Refuser / demander une modification
                </Button>
              </div>
            )}
          </div>
        )}
      </main>

      <Dialog open={!!elementsPopup} onOpenChange={(v) => !v && navigate("/graphisme/espace")}>
        <DialogContent className="max-w-md" data-testid="gclient-elements-popup" aria-describedby={undefined}>
          <DialogTitle className="font-heading font-bold text-lg">Éléments en attente de votre part</DialogTitle>
          <p className="text-sm text-neutral-500">Pour démarrer votre projet, votre graphiste a besoin de :</p>
          <div className="space-y-3 mt-1">
            {(elementsPopup || []).map((e) => (
              <div key={e.id} className="border border-orange-200 bg-orange-50 rounded-xl p-3" data-testid={`popup-element-${e.id}`}>
                <p className="text-sm font-medium text-orange-900">{e.label}</p>
                <ElementUploader element={e} compact onDone={() =>
                  setElementsPopup((list) => list.filter((x) => x.id !== e.id))} />
              </div>
            ))}
            {elementsPopup?.length === 0 && (
              <p className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg p-3">
                Tous les éléments ont été transmis — merci !
              </p>
            )}
          </div>
          <Button variant="outline" className="w-full" onClick={() => navigate("/graphisme/espace")} data-testid="popup-elements-later">
            Plus tard
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
